"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VideoDocumentSchema = exports.VideoOutputPlacementSchema = exports.DocumentBrandSchema = exports.CanvasSchema = exports.SceneSchema = exports.DocumentCaptionsSchema = exports.CaptionStyleSchema = exports.CAPTION_CHUNKING_MODES = exports.TransitionSchema = exports.TRANSITION_STYLES = exports.LayerSchema = exports.AudioLayerSchema = exports.TextLayerSchema = exports.ImageLayerSchema = exports.VideoLayerSchema = exports.LayerTranscriptSchema = exports.TranscriptWordSchema = exports.TextStyleSchema = exports.AssetRefSchema = exports.LayerSourceSchema = exports.LayerPositionSchema = exports.LayerAnchorSchema = exports.LAYER_ANCHORS = exports.VIDEO_DOCUMENT_SCHEMA_VERSION = void 0;
exports.parseVideoDocument = parseVideoDocument;
exports.documentHasPendingSlots = documentHasPendingSlots;
exports.documentHasBlockingSlots = documentHasBlockingSlots;
const zod_1 = require("zod");
const brand_video_settings_1 = require("../brand-video-settings");
/**
 * The video-editor document schema — the v1 "vocabulary". A video is this typed JSON, stored as
 * immutable versions; the agent and the timeline edit it, the composition package renders it, and
 * every save/placement renders from it. The schema is OURS (Remotion never sees it). It grows
 * ADDITIVELY: a new word is a new optional field, so every stored version renders forever.
 *
 * Design authority: openspec/changes/agent-video-editor/design.md §2.
 */
/** Bump only additively. A document always records the version it was written under. */
exports.VIDEO_DOCUMENT_SCHEMA_VERSION = 1;
// ─── Positioning (anchor + canvas-relative offset — NOT raw pixels) ──────────────────────────────
// Raw pixels can't re-layout across aspect ratios; anchors can. A layer with NO `position` is
// full-frame (AbsoluteFill). A positioned layer declares intent ("top-right, small margin") that
// `resolveLayout` turns into pixels on ANY canvas.
exports.LAYER_ANCHORS = [
    'top-left',
    'top-center',
    'top-right',
    'middle-left',
    'center',
    'middle-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
];
exports.LayerAnchorSchema = zod_1.z.enum(exports.LAYER_ANCHORS);
/** All measurements are percentages of the canvas (0–100), so they survive any aspect ratio. */
exports.LayerPositionSchema = zod_1.z
    .object({
    anchor: exports.LayerAnchorSchema,
    /** Inset from the anchored edge(s), % of the smaller canvas dimension. */
    marginPct: zod_1.z.number().min(0).max(100).default(0),
    /** Fine nudge after anchoring, % of canvas width (+right / −left). */
    offsetXPct: zod_1.z.number().min(-100).max(100).default(0),
    /** Fine nudge after anchoring, % of canvas height (+down / −up). */
    offsetYPct: zod_1.z.number().min(-100).max(100).default(0),
    /** Element width as % of canvas width; height follows intrinsic aspect unless given. */
    widthPct: zod_1.z.number().min(0).max(100).optional(),
    heightPct: zod_1.z.number().min(0).max(100).optional(),
})
    .strict();
// ─── Provenance ──────────────────────────────────────────────────────────────────────────────────
// Every layer records where its content came from, so a FRESH agent session (no chat memory) knows
// what it is editing and can reproduce/modify it — this is how "make a version with a man" works
// weeks later.
exports.LayerSourceSchema = zod_1.z
    .object({
    /** e.g. 'seedance' | 'upload' | 'nano-banana' | 'user' — free-form; the generator that made it. */
    generator: zod_1.z.string().optional(),
    /** The exact prompt that produced the asset (Seedance/image gen), verbatim. */
    prompt: zod_1.z.string().optional(),
    /** The generation this asset came from, if any. */
    generationId: zod_1.z.string().optional(),
    /** Human/AI description of what the asset depicts (esp. for uploads with no prompt). */
    description: zod_1.z.string().optional(),
    /** Layer id (in a prior version) this was derived from — e.g. the woman clip a man clip forked from. */
    derivedFrom: zod_1.z.string().optional(),
})
    .strict();
// ─── Asset reference (ready | pending | failed) ──────────────────────────────────────────────────
// A layer's media may not be in hand yet. A `pending` slot is a `provide: …` gap: an asset the user
// still has to supply (upload / point at an existing creative). The editor writes it when a scene
// needs an asset the project doesn't have, and fills it to `ready` on a later turn once the asset
// exists — the editor never generates. Saving is blocked while any slot is pending.
exports.AssetRefSchema = zod_1.z.discriminatedUnion('status', [
    zod_1.z
        .object({
        status: zod_1.z.literal('ready'),
        fileUuid: zod_1.z.string(),
        /** ACTUAL media duration in seconds (ffprobe ground truth) — stamped by the platform when
         *  a pending slot resolves, recorded by the agent for pre-existing clips. Scene durations
         *  and trims must respect it: footage past this point freezes on the last frame. */
        durationSeconds: zod_1.z.number().positive().optional(),
    })
        .strict(),
    zod_1.z
        .object({
        status: zod_1.z.literal('pending'),
        /** What the user needs to provide — shown as the `provide: …` placeholder label. The
         *  editor writes this when a scene needs an asset the project doesn't have; it is filled
         *  to `ready` once the user provides the file (upload / existing creative). */
        description: zod_1.z.string().optional(),
    })
        .strict(),
    zod_1.z.object({ status: zod_1.z.literal('failed'), error: zod_1.z.string().optional() }).strict(),
]);
// ─── Text style ──────────────────────────────────────────────────────────────────────────────────
exports.TextStyleSchema = zod_1.z
    .object({
    /** Font size as % of canvas height (resolution-independent). */
    fontSizePct: zod_1.z.number().min(0).max(100).optional(),
    fontFamily: zod_1.z.string().optional(),
    fontWeight: zod_1.z.number().optional(),
    color: zod_1.z.string().optional(),
    textAlign: zod_1.z.enum(['left', 'center', 'right']).optional(),
    lineHeight: zod_1.z.number().optional(),
    letterSpacing: zod_1.z.number().optional(),
    textShadow: zod_1.z.string().optional(),
    backgroundColor: zod_1.z.string().optional(),
})
    .strict();
// ─── Transcript ──────────────────────────────────────────────────────────────────────────────────
// Word-level speech timings live on the speech-bearing layer itself, on the layer's SOURCE FILE's
// own clock (the same clock as sourceIn/sourceOut) — never scene- or video-relative. Trims and
// reorders never invalidate a word; they only change which words fall inside the audible span.
// Transcript presence on a layer IS the per-source captioning opt-in. Caption PHRASES are never
// stored: `documentCaptionLayout` (caption-layout.ts) derives display timing from these words fresh
// on every read, so a word correction can never be regenerated away.
exports.TranscriptWordSchema = zod_1.z
    .object({
    text: zod_1.z.string().min(1),
    startSeconds: zod_1.z.number().min(0),
    endSeconds: zod_1.z.number().min(0),
})
    .strict()
    .superRefine((word, ctx) => {
    if (word.endSeconds <= word.startSeconds) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `Transcript word "${word.text}": endSeconds (${word.endSeconds}) must be greater than startSeconds (${word.startSeconds})`,
        });
    }
});
exports.LayerTranscriptSchema = zod_1.z.object({ words: zod_1.z.array(exports.TranscriptWordSchema).max(3000) }).strict();
const TRANSCRIPT_FIELD_DESCRIPTION = "Word-level speech timings for this layer's audio, in seconds on the SOURCE FILE's own clock " +
    "-- the same clock as sourceIn/sourceOut, never scene- or video-relative. Trimming the layer " +
    'never invalidates a word; it only changes which words fall inside the trimmed span. Fix a ' +
    'misheard or mistyped word by editing transcript.words[].text directly -- the correction ' +
    'persists through every later timeline edit.';
// ─── Layers ──────────────────────────────────────────────────────────────────────────────────────
// Every layer carries a permanent `id` (minted at creation, copied through forks) so a user's click
// resolves to the same logical element across versions. `source` is the provenance block.
const LayerBaseShape = {
    /** Permanent, stable across forks. */
    id: zod_1.z.string(),
    position: exports.LayerPositionSchema.optional(),
    /**
     * Optional window START, in seconds. Absent = the layer is there from the start of its
     * container. There is deliberately NO schema default: a document stores only timing someone
     * actually asked for, and every consumer reads absent as 0.
     *
     * WHICH CLOCK IS DECIDED BY WHERE THE LAYER SITS — one field name, two frames of reference:
     * - in a scene's `layers` → SCENE-RELATIVE seconds, and the layer is CONTENT-ANCHORED:
     *   `appearsAt: 2` means 2s into ITS scene, wherever that scene ends up in the video. Trim or
     *   reorder the scene and the layer moves with its footage.
     * - in the document's `overlays` → DOCUMENT-RELATIVE seconds on the video's absolute clock, and
     *   the layer is CLOCK-ANCHORED: `appearsAt: 5` means 5s into the VIDEO, whatever is playing
     *   then. Scene edits never move it.
     *
     * Any layer type may carry a window (added 2026-07-15 — was text-only `appearsAt`): an image
     * watermark, a PiP video, or a music bed can all appear late or end early.
     */
    appearsAt: zod_1.z
        .number()
        .min(0)
        .optional()
        .describe('Optional window start, in seconds; absent = from the start. Scene-relative for a scene layer, video-relative for a document overlay.'),
    /**
     * Optional window END, in seconds, on the SAME clock as {@link appearsAt} above. Absent = to the
     * end of the container (the layer's scene, or the whole video for an overlay).
     *
     * Two failure modes, two different answers — deliberately:
     * - INVERTED (`endsAt <= appearsAt`) is a window that cannot exist, so the write is REJECTED
     *   (superRefine below).
     * - OVERRUNNING the container's end CLAMPS: the write SUCCEEDS and returns a warning naming
     *   what actually renders (`documentTimingWarnings`). Never rejected, never reorders anything —
     *   the same clamp-and-warn contract over-long transitions already follow.
     */
    endsAt: zod_1.z
        .number()
        .min(0)
        .optional()
        .describe('Optional window end, in seconds, on the same clock as appearsAt; absent = to the end of the scene (or of the video, for an overlay). Must be greater than appearsAt. Past the end it clamps and the write warns.'),
    /** Per-aspect-ratio overrides, keyed by ratio (e.g. "1:1"). The escape hatch for placement fixes.
     * The .describe() matters: the JSON-Schema converter clobbers a record's value schema
     * (additionalProperties gets forced to false downstream), so the description is the only shape
     * guidance the model receives for this field. */
    overrides: zod_1.z
        .record(zod_1.z.string(), exports.LayerPositionSchema.partial())
        .optional()
        .describe('Per-aspect-ratio position overrides, keyed by ratio string, e.g. {"1:1": {"offsetXPct": -4}}. Values are partial position objects (anchor/marginPct/offsetXPct/offsetYPct/widthPct/heightPct).'),
    source: exports.LayerSourceSchema.optional(),
};
exports.VideoLayerSchema = zod_1.z
    .object({
    ...LayerBaseShape,
    type: zod_1.z.literal('video'),
    asset: exports.AssetRefSchema,
    fit: zod_1.z.enum(['cover', 'contain']).default('cover'),
    /** 0–1. Audio from the video's own track. */
    volume: zod_1.z.number().min(0).max(1).default(1),
    /** Trim, in seconds of the SOURCE clip. `sourceOut` must be > 0 and > `sourceIn`. */
    sourceIn: zod_1.z.number().min(0).optional(),
    sourceOut: zod_1.z.number().positive().optional(),
    opacity: zod_1.z.number().min(0).max(1).default(1),
    transcript: exports.LayerTranscriptSchema.optional().describe(TRANSCRIPT_FIELD_DESCRIPTION),
})
    .strict();
exports.ImageLayerSchema = zod_1.z
    .object({
    ...LayerBaseShape,
    type: zod_1.z.literal('image'),
    asset: exports.AssetRefSchema,
    fit: zod_1.z.enum(['cover', 'contain']).default('contain'),
    opacity: zod_1.z.number().min(0).max(1).default(1),
})
    .strict();
exports.TextLayerSchema = zod_1.z
    .object({
    ...LayerBaseShape,
    type: zod_1.z.literal('text'),
    text: zod_1.z.string(),
    style: exports.TextStyleSchema.optional(),
})
    .strict();
exports.AudioLayerSchema = zod_1.z
    .object({
    ...LayerBaseShape,
    type: zod_1.z.literal('audio'),
    asset: exports.AssetRefSchema,
    /** 0–1. Music beds default low so they sit under dialogue. */
    volume: zod_1.z.number().min(0).max(1).default(0.15),
    sourceIn: zod_1.z.number().min(0).optional(),
    loop: zod_1.z.boolean().default(false),
    transcript: exports.LayerTranscriptSchema.optional().describe(TRANSCRIPT_FIELD_DESCRIPTION),
})
    .strict();
exports.LayerSchema = zod_1.z.discriminatedUnion('type', [
    exports.VideoLayerSchema,
    exports.ImageLayerSchema,
    exports.TextLayerSchema,
    exports.AudioLayerSchema,
]);
// ─── Transitions ─────────────────────────────────────────────────────────────────────────────────
exports.TRANSITION_STYLES = ['cut', 'crossfade', 'wipe'];
exports.TransitionSchema = zod_1.z
    .object({
    style: zod_1.z.enum(exports.TRANSITION_STYLES),
    /**
     * Seconds; ignored for 'cut'. The two scenes OVERLAP for this long (that overlap is the
     * transition), so it shortens the video by the same amount. Clamped to the shorter of the
     * two scenes it joins — see `documentFrameLayout`.
     */
    duration: zod_1.z.number().min(0).default(0.5),
})
    .strict();
// ─── Captions ────────────────────────────────────────────────────────────────────────────────────
// Caption WORDS live on the speech-bearing layer's `transcript` (see above) — this document-level
// shape only records whether captions are on and how they look. Phrase timing is derived on every
// read by `documentCaptionLayout` (caption-layout.ts), never stored, so it can never go stale
// against a timeline edit.
const CaptionTextCustomizationSchema = zod_1.z
    .object({
    font: zod_1.z.string().min(1).optional(),
    weight: zod_1.z.number().int().min(100).max(900).optional(),
    color: zod_1.z.string().min(1).optional(),
})
    .strict();
exports.CAPTION_CHUNKING_MODES = ['one-word', 'short', 'phrase'];
/** Mirrors `IBrandVideoCaptionRenderSettings` (brand-video-settings.ts) field-for-field — one
 *  caption-look vocabulary shared by brand settings and the editor document, never a second copy —
 *  PLUS editor-only adjustment fields (`sizePct` onward) that `generateCaptions` seeding never
 *  writes: bounded geometry/behavior knobs around the preset, never raw CSS. Absent on any of them
 *  means the renderer's house constant. */
exports.CaptionStyleSchema = zod_1.z
    .object({
    preset: zod_1.z
        .enum(brand_video_settings_1.BRAND_VIDEO_CAPTION_PRESETS)
        .describe('VEED-parity caption look, one of the 21 named presets (e.g. "simple", "hustle", "kitty").'),
    position: zod_1.z
        .enum(brand_video_settings_1.BRAND_VIDEO_CAPTION_POSITIONS)
        .describe('Vertical anchor for the caption block within the frame.'),
    shadow: zod_1.z.enum(brand_video_settings_1.BRAND_VIDEO_CAPTION_SHADOWS).describe('Drop-shadow strength behind the caption text.'),
    baseline: CaptionTextCustomizationSchema.optional().describe('Overrides for a non-highlighted word: font family, weight (100-900), hex color.'),
    highlighted: CaptionTextCustomizationSchema.optional().describe('Overrides for the currently-spoken word: font family, weight (100-900), hex color.'),
    sizePct: zod_1.z
        .number()
        .min(2)
        .max(12)
        .optional()
        .describe('Caption text size as % of canvas height. Absent = 4.'),
    lineHeight: zod_1.z
        .number()
        .min(1)
        .max(2)
        .optional()
        .describe('Line height multiplier for wrapped caption lines. Absent = 1.3.'),
    maxWidthPct: zod_1.z
        .number()
        .min(40)
        .max(100)
        .optional()
        .describe('Caption block max width as % of canvas width. Absent = 86.'),
    marginPct: zod_1.z
        .number()
        .min(0)
        .max(25)
        .optional()
        .describe('Distance between the caption block and its anchored edge as % of canvas height; no effect at center position. Absent = safe-zone default (14 at top, 20 at bottom).'),
    casing: zod_1.z
        .enum(['uppercase', 'original'])
        .optional()
        .describe('Overrides the preset casing. Absent = the preset decides.'),
    highlightMode: zod_1.z
        .enum(['none', 'text-color', 'word-box'])
        .optional()
        .describe('Overrides the preset active-word treatment; none renders the active word like any other word. Absent = the preset decides.'),
    chunking: zod_1.z
        .enum(exports.CAPTION_CHUNKING_MODES)
        .optional()
        .describe('How much text shares the screen: one-word = CapCut-style single word per beat; short = a few words (about 24 chars); phrase = longer subtitle-style lines (about 42 chars). Absent = short.'),
})
    .strict();
exports.DocumentCaptionsSchema = zod_1.z
    .object({
    enabled: zod_1.z.boolean(),
    style: exports.CaptionStyleSchema.optional().describe("Caption look. Absent renders the composition's plain default; generateCaptions seeds " +
        "it from the brand's saved caption settings the first time it runs."),
})
    .strict();
// ─── Scene ───────────────────────────────────────────────────────────────────────────────────────
exports.SceneSchema = zod_1.z
    .object({
    /** Permanent, stable across forks. */
    id: zod_1.z.string(),
    /** Scene length in seconds. */
    duration: zod_1.z.number().min(0),
    /** Bottom-to-top: index 0 is backmost. */
    layers: zod_1.z.array(exports.LayerSchema).max(20),
    /** Transition INTO the next scene. */
    transitionToNext: exports.TransitionSchema.optional(),
})
    .strict();
// ─── Canvas + Brand ──────────────────────────────────────────────────────────────────────────────
exports.CanvasSchema = zod_1.z
    .object({
    width: zod_1.z.number().int().positive(),
    height: zod_1.z.number().int().positive(),
    fps: zod_1.z.number().int().positive().default(30),
})
    .strict();
exports.DocumentBrandSchema = zod_1.z
    .object({
    primaryColor: zod_1.z.string().optional(),
    secondaryColor: zod_1.z.string().optional(),
    name: zod_1.z.string().optional(),
})
    .strict();
// ─── Output placement ────────────────────────────────────────────────────────────────────────────
// The approved creative's master placement is versioned with the edit, rather than being inferred
// into the platform-less "custom" channel at approval time. Slugs keep the document portable across
// environments; approval resolves them against the active marketing-channel catalog.
exports.VideoOutputPlacementSchema = zod_1.z
    .object({
    channelSlug: zod_1.z.string().min(1),
    placementSlug: zod_1.z.string().min(1),
})
    .strict();
// ─── Document (top level) ────────────────────────────────────────────────────────────────────────
exports.VideoDocumentSchema = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(exports.VIDEO_DOCUMENT_SCHEMA_VERSION),
    canvas: exports.CanvasSchema,
    /**
     * Where approval should place the rendered master. Optional and additive: old documents keep
     * parsing, while approval derives a Meta target from the main source video or final canvas
     * when this is absent. Keeping the intent in the immutable document means the agent can
     * change it in the normal edit flow and approval pins exactly what the user reviewed.
     */
    outputPlacement: exports.VideoOutputPlacementSchema.optional().describe('Optional approval target as { channelSlug, placementSlug }. Preserve it across edits unless the user changes the destination. When absent, approval inherits a compatible Meta placement from the main input video or defaults to Meta from the final canvas.'),
    scenes: zod_1.z.array(exports.SceneSchema).max(50),
    /**
     * Layers on the VIDEO's own absolute clock, spanning scene cuts — a watermark logo, a
     * persistent CTA, a music bed. The word that makes "logo from second 5 to 20" writable when
     * the cuts fall at 15.06s and 19.10s: ONE element, not a per-scene fragment each.
     *
     * How they differ from a scene's layers (the whole point of the distinction):
     * - CLOCK-ANCHORED: `appearsAt`/`endsAt` are document-relative (absent = the whole video).
     *   Trimming or reordering scenes never moves an overlay. A scene layer is the opposite —
     *   content-anchored, moving with its footage. The agent picks the home by INTENT: clock
     *   language → overlay; content language ("during the product shot") → scene layer.
     * - Rendered ABOVE every scene (that is their point), below captions.
     * - NOT part of any scene transition: no reveal, no audio ramp — an overlay is continuous by
     *   definition. Its music bed therefore never restarts at a cut.
     * - They NEVER affect the frame layout: scene starts and total duration come from `scenes`
     *   alone (`documentFrameLayout`). An overlay window past the video's end clamps and warns.
     *
     * Every layer type is legal here: video (cross-cut PiP), image (watermark), text
     * (persistent CTA), audio (the music bed).
     */
    overlays: zod_1.z
        .array(exports.LayerSchema)
        .max(10)
        .optional()
        .describe('Layers on the VIDEO\'s absolute clock, spanning scene cuts (watermark, persistent CTA, music bed). Their appearsAt/endsAt are video-relative (absent = the whole video); scene edits never move them; they render above every scene and take no part in transitions. Use for clock intent ("from second 5 to 20"); use a scene layer for content intent ("during the product shot").'),
    /** Whether captions are on and how they look; word-level timing is never stored here. */
    captions: exports.DocumentCaptionsSchema.optional().describe('Caption on/off + look: { enabled, style? }. Word-level timing comes from transcript on ' +
        'the speech-bearing layer(s), not from this field.'),
    brand: exports.DocumentBrandSchema.optional(),
})
    .strict()
    // Scene ids and layer ids must be unique across the whole document — clicks/chips and the delta
    // engine address elements by id, so a collision would make a selection ambiguous. Layer ids are
    // unique across scenes AND overlays: an id addresses ONE element, and where it happens to live
    // is not part of its name.
    .superRefine((doc, ctx) => {
    const sceneIds = new Set();
    const layerIds = new Set();
    /** Every layer is checked identically wherever it lives — an overlay is a layer like any
     *  other, so it must not be able to smuggle past a rule a scene layer obeys. */
    const checkLayer = (layer) => {
        if (layerIds.has(layer.id)) {
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: `Duplicate layer id: ${layer.id}` });
        }
        layerIds.add(layer.id);
        // A window that cannot exist is an error; a window that overruns its container is not —
        // that one clamps and warns (see `documentTimingWarnings`). Absent `appearsAt` means 0,
        // so a lone `endsAt: 0` is inverted too.
        const appearsAt = layer.appearsAt ?? 0;
        if (layer.endsAt != null && layer.endsAt <= appearsAt) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `Layer ${layer.id}: endsAt (${layer.endsAt}) must be greater than appearsAt (${appearsAt}) — the layer would never be on screen.`,
            });
        }
        if (layer.type === 'video' &&
            layer.sourceIn != null &&
            layer.sourceOut != null &&
            layer.sourceOut <= layer.sourceIn) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: `Layer ${layer.id}: sourceOut (${layer.sourceOut}) must be greater than sourceIn (${layer.sourceIn})`,
            });
        }
    };
    for (const scene of doc.scenes) {
        if (sceneIds.has(scene.id)) {
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: `Duplicate scene id: ${scene.id}` });
        }
        sceneIds.add(scene.id);
        for (const layer of scene.layers)
            checkLayer(layer);
    }
    for (const overlay of doc.overlays ?? [])
        checkLayer(overlay);
});
/**
 * Parse-and-validate a candidate document. Every write goes through this — an invalid document is
 * rejected, never stored. Returns the parsed doc (with defaults applied) or throws a ZodError.
 */
function parseVideoDocument(input) {
    return exports.VideoDocumentSchema.parse(input);
}
/** True if any layer in the document is still generating (pending) — saving must be blocked.
 *  Walks BOTH homes: an overlay is a layer like any other, and a pending overlay is just as
 *  missing as a pending scene layer — it renders a placeholder, so it must block the save that
 *  would burn that placeholder into a creative. */
function documentHasPendingSlots(doc) {
    const isPending = (layer) => 'asset' in layer && layer.asset.status === 'pending';
    return doc.scenes.some((scene) => scene.layers.some(isPending)) || (doc.overlays ?? []).some(isPending);
}
/** True if any slot is unresolved — `pending` OR `failed`. The APPROVAL gate (and everything that
 *  mints pixels from the document: the render route, the approve button) blocks on this, not on
 *  {@link documentHasPendingSlots}: a `failed` slot renders the "asset unavailable" placeholder,
 *  and approving would burn that placeholder into a locked creative. The pending-only helper stays
 *  for poll accelerators — a failed slot is terminal, there is nothing to poll for. */
function documentHasBlockingSlots(doc) {
    const isBlocking = (layer) => 'asset' in layer && (layer.asset.status === 'pending' || layer.asset.status === 'failed');
    return doc.scenes.some((scene) => scene.layers.some(isBlocking)) || (doc.overlays ?? []).some(isBlocking);
}
